import { CdkConnectedOverlay, CdkOverlayOrigin } from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { PaperAvatarComponent, PaperButtonComponent } from '@ricoh-paper/angular';

import { ANALYTICS_EVENTS } from '../../../../core/analytics/events';
import { anchorFrom, type PopoverAnchor } from '../../../../core/overlays/anchor.util';
import { PopoverBandService } from '../../../../core/overlays/popover-band.service';
import { AnalyticsService } from '../../../../core/services/analytics.service';
import { FlowLiveSessionStore } from '../../../../core/services/flow-live-session.store';
import { FlowMarkComponent } from '../../../flow-v2/identity/flow-mark.component';
import {
  FLOW_SESSION_CARD_KEY,
  HOVER_CLOSE_MS,
  HOVER_OPEN_MS,
  canMute,
  cardHeads,
  pillHeads,
  stateChip,
  timeLabel,
  whereKey,
} from './flow-session-indicator.model';

/** The card's width, px: the `lg` rung, which holds Back to session, Board, the mute and End on one row. */
const CARD_WIDTH = 372;

/**
 * `im-flow-session-indicator` — the live session that follows you, in the top bar (Flow direction §2.1 step 7,
 * §2.2, mock-up 03).
 *
 * WHEN. Whenever `FlowLiveSessionStore.session()` is set, on every route except that session's own notes page
 * (`onSessionPage`), the board included. The shell projects it into the Paper bar's utility cluster and Louis's
 * workspace nav projects it into the soft-frost rail's, so it is in whichever top bar the route draws.
 *
 * THE PILL. Flow's mark, three activity dots that move only while this tab records the session (and never under
 * reduced motion), the title (meeting content: masked), the time, and up to three other people's heads.
 *
 * THE LIVE CARD. A popover (`POPOVERS.md`: `data-popover`, the trigger marked `data-pop-trigger`, the popover
 * band for one-at-a-time, click-away, Escape and focus). A mouse resting on the pill opens it after a short
 * intent delay and it folds once the pointer has left both; a click or the keyboard opens it with focus inside.
 * It is portalled out of the bar (the bar's glass is a containing block for anything fixed inside it). It holds
 * the title and "Rec 14:22" (or "Live"), who is in and where, the last two lines, Back to session, Board, the
 * mute, and End for organisers (a confirm that says what End does, with End for everyone and Leave), or Leave.
 *
 * It takes over from the floating recording widget: while it shows, the store's `indicatorShown` tells the
 * widget to step aside for this session's recording (`widgetSubject`'s `heldBy`).
 *
 * ANALYTICS. `emp.flow.session.indicator-action`, ids and closed lists only. PRIVACY: the title and the lines
 * are `ph-mask`, never in an attribute.
 */
@Component({
  selector: 'im-flow-session-indicator',
  standalone: true,
  imports: [CdkConnectedOverlay, CdkOverlayOrigin, PaperAvatarComponent, PaperButtonComponent, FlowMarkComponent, TranslocoPipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'ph-no-capture',
    '[attr.data-placement]': 'placement()',
    '[class.is-shown]': 'shown()',
  },
  templateUrl: './flow-session-indicator.component.html',
  styleUrl: './flow-session-indicator.component.scss',
})
export class FlowSessionIndicatorComponent {
  protected readonly store = inject(FlowLiveSessionStore);
  private readonly band = inject(PopoverBandService);
  private readonly router = inject(Router);
  private readonly analytics = inject(AnalyticsService);
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;

  /** Which top bar it sits in: the Paper bar, or Louis's workspace nav (the frost rail). */
  readonly placement = input<'bar' | 'nav'>('bar');

  protected readonly cardKey = FLOW_SESSION_CARD_KEY;
  protected readonly cardId = `flow-session-card-${Math.random().toString(36).slice(2, 8)}`;
  protected readonly confirmTitleId = `${this.cardId}-confirm`;
  private readonly origin = viewChild(CdkOverlayOrigin);

  protected readonly shown = computed(() => !!this.store.session() && !this.store.onSessionPage());
  protected readonly open = signal(false);
  protected readonly confirming = signal(false);
  protected readonly ending = signal(false);
  protected readonly endFailed = signal(false);
  protected readonly box = signal<PopoverAnchor | null>(null);

  protected readonly session = this.store.session;
  protected readonly recording = this.store.recording;
  protected readonly recordsLive = this.store.recordsLive;
  protected readonly moving = computed(() => this.recording() === 'recording');
  protected readonly time = computed(() => timeLabel(this.store.elapsedMs()));
  protected readonly pillHeads = computed(() => pillHeads(this.store.heads()));
  protected readonly cardHeads = computed(() => cardHeads(this.store.heads()));
  protected readonly lines = computed(() => this.store.lines().slice(-2));
  protected readonly chip = computed(() => stateChip(this.recording()));
  protected readonly canMute = computed(() => canMute(this.recording()));
  protected readonly paused = computed(() => this.recording() === 'paused');
  protected readonly whereKey = whereKey;

  /** How the card opened: a resting mouse folds it again when the pointer leaves; a click keeps it. */
  private openedBy: 'hover' | 'click' | null = null;
  private hoverTimer: ReturnType<typeof setTimeout> | null = null;
  private closeTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    // While it shows, the store keeps the heads and the clock fresh, and the recording widget steps aside.
    effect((onCleanup) => {
      if (!this.shown()) return;
      const release = untracked(() => this.store.watch('indicator'));
      onCleanup(release);
    });
    effect((onCleanup) => {
      if (!this.open()) return;
      const release = untracked(() => this.store.watch('card'));
      onCleanup(release);
    });
    // The pill goes (the session ended, its page opened, it was left): so does its card.
    effect(() => {
      const shown = this.shown();
      untracked(() => {
        if (!shown && this.open()) this.closeCard({ restoreFocus: false });
      });
    });
    inject(DestroyRef).onDestroy(() => {
      this.clearTimers();
      this.band.close(FLOW_SESSION_CARD_KEY, { restoreFocus: false });
    });
  }

  /* ── Opening and closing ───────────────────────────────────────────── */

  protected onPillClick(): void {
    this.clearTimers();
    if (this.open() && this.openedBy === 'click') {
      this.closeCard();
      return;
    }
    if (this.open()) {
      // Opened by a resting mouse, now pressed: it stays, and takes the keyboard.
      this.openedBy = 'click';
      this.band.open(FLOW_SESSION_CARD_KEY, { close: () => this.dismissFromBand(), trigger: this.focusTarget(), focus: true });
      return;
    }
    this.openCard('click');
  }

  protected onPointerEnter(): void {
    if (this.closeTimer) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
    if (this.open() || this.hoverTimer) return;
    this.hoverTimer = setTimeout(() => {
      this.hoverTimer = null;
      // Never over another open popover: a passing mouse must not close somebody's menu.
      if (!this.open() && this.shown() && !this.band.anyOpen()) this.openCard('hover');
    }, HOVER_OPEN_MS);
  }

  protected onPointerLeave(): void {
    if (this.hoverTimer) {
      clearTimeout(this.hoverTimer);
      this.hoverTimer = null;
    }
    if (!this.open() || this.openedBy !== 'hover' || this.closeTimer) return;
    this.closeTimer = setTimeout(() => {
      this.closeTimer = null;
      if (this.openedBy === 'hover' && !this.confirming()) this.closeCard({ restoreFocus: false });
    }, HOVER_CLOSE_MS);
  }

  private openCard(by: 'hover' | 'click'): void {
    const session = this.session();
    if (!session) return;
    this.box.set(anchorFrom(this.trigger(), { size: CARD_WIDTH, align: 'end' }));
    this.confirming.set(false);
    this.endFailed.set(false);
    this.openedBy = by;
    this.open.set(true);
    this.band.open(FLOW_SESSION_CARD_KEY, { close: () => this.dismissFromBand(), trigger: this.focusTarget(), focus: by === 'click' });
    this.capture('open-card', { trigger: by });
  }

  /** Close from here (the pill, an action): through the band, so focus returns to the pill unless an action moves it. */
  protected closeCard(opts: { restoreFocus?: boolean } = {}): void {
    if (!this.open()) return;
    this.open.set(false);
    this.confirming.set(false);
    this.openedBy = null;
    this.band.close(FLOW_SESSION_CARD_KEY, { restoreFocus: opts.restoreFocus !== false });
  }

  /** What the band calls (click-away, Escape, another popover opening): local state only, never back into the band. */
  private dismissFromBand(): void {
    if (!this.open()) return;
    this.open.set(false);
    this.confirming.set(false);
    this.openedBy = null;
  }

  /* ── The card's actions ────────────────────────────────────────────── */

  protected back(): void {
    const links = this.store.links();
    if (!links) return;
    this.capture('back');
    this.closeCard({ restoreFocus: false });
    void this.router.navigateByUrl(links.session);
  }

  protected openBoard(): void {
    const board = this.store.links()?.board;
    if (!board) return;
    this.capture('board');
    this.closeCard({ restoreFocus: false });
    void this.router.navigateByUrl(board);
  }

  protected async togglePause(): Promise<void> {
    this.capture(this.paused() ? 'resume' : 'pause');
    await this.store.togglePause();
  }

  protected askEnd(): void {
    this.capture('end');
    this.endFailed.set(false);
    this.confirming.set(true);
    // A confirm is a decision: it no longer folds when the mouse wanders off.
    this.openedBy = 'click';
  }

  protected async endForEveryone(): Promise<void> {
    if (this.ending()) return;
    const session = this.session();
    if (!session) return;
    this.ending.set(true);
    this.endFailed.set(false);
    const ok = await this.store.end();
    this.ending.set(false);
    this.capture('end-everyone', { result: ok ? 'ok' : 'failed' }, session.id, session.boardId);
    if (ok) this.closeCard({ restoreFocus: false });
    else this.endFailed.set(true);
  }

  protected leave(): void {
    const session = this.session();
    if (!session) return;
    this.capture('leave');
    this.closeCard({ restoreFocus: false });
    this.store.leave(session.id);
  }

  /* ── Helpers ───────────────────────────────────────────────────────── */

  /** The pill, which the card is measured from. */
  private trigger(): HTMLElement {
    return this.origin()?.elementRef.nativeElement ?? this.host;
  }

  /** The pill's own button, where focus goes back when the card closes. */
  private focusTarget(): HTMLElement {
    const pill = this.trigger();
    return pill.querySelector<HTMLElement>('button') ?? pill;
  }

  private clearTimers(): void {
    if (this.hoverTimer) clearTimeout(this.hoverTimer);
    if (this.closeTimer) clearTimeout(this.closeTimer);
    this.hoverTimer = null;
    this.closeTimer = null;
  }

  private capture(
    action: 'open-card' | 'back' | 'board' | 'pause' | 'resume' | 'end' | 'end-everyone' | 'leave',
    extra: { trigger?: 'hover' | 'click'; result?: 'ok' | 'failed' } = {},
    id?: string,
    boardId?: string | null,
  ): void {
    const session = this.session();
    const flowPlanId = id ?? session?.id;
    if (!flowPlanId) return;
    this.analytics.capture(ANALYTICS_EVENTS.FLOW_SESSION_INDICATOR_ACTION, {
      action,
      surface: 'indicator',
      flow_plan_id: flowPlanId,
      board_id: boardId !== undefined ? boardId : (session?.boardId ?? null),
      recording: this.recording(),
      ...extra,
    });
  }
}
